// App.js
import React from 'react';
import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import Layout from './components/Common/Layout';
import Dashboard from './pages/Dashboard';
import Chats from './pages/Chats';
import Users from './pages/Users';
import Answers from './pages/Answers';
import Leads from './pages/Leads';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Logout from './pages/Logout';
import UserMessage from './components/UserMessage/UserMessage';
import SetUp from './pages/SetupPage/SetUp';
import FlowSetup from './pages/FlowSetupPage/FlowSetup';
import AutoLogin from './components/AutoLogin';
import PrivateRoute from './PrivateRoute/PrivateRoute';
import Account from './pages/Account';

import Install from './pages/InstallationPage/Install';
// import WidgetApp from './widget/WidgetApp';
// import WidgetLoader from './widget/WidgetLoader';
import ConversationView from './pages/ConversationView';
import Conversations from './pages/Conversations';
import Bots from './pages/Bots';
import BotDetail from './pages/BotDetail';
import AIAgents from './pages/AIAgents';
import AIAgentEdit from './pages/AIAgentEdit';
import AIAgentChats from './pages/AIAgentChats';
import AgentChat from './pages/AgentChat';
import Templates from './pages/Templates';
import Partners from './pages/Partners';
import Referral from './pages/Referral';
import Plans from './pages/Plans';
import UserManagement from './pages/UserManagement';
import TeamManagement from './pages/TeamManagement';
import LiveChat from './pages/LiveChat';
import Campaigns from './pages/Campaigns';


function ExternalRedirect({ to }) {
  React.useEffect(() => { window.location.href = to; }, [to]);
  return null;
}

function TokenRedirect() {
  const { token } = useParams();
  const navigate = useNavigate();
  const botsApi = (process.env.REACT_APP_AUTH_API || 'http://localhost:5000/api/auth').replace('/api/auth', '/api/bots');
  React.useEffect(() => {
    if (!token) { navigate('/', { replace: true }); return; }
    fetch(botsApi + '/widget/' + token + '/settings')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (data.botId) navigate('/usertest/' + data.botId, { replace: true }); else navigate('/', { replace: true }); })
      .catch(() => navigate('/', { replace: true }));
  }, [token, navigate, botsApi]);
  return null;
}

function InlineRedirect() {
  const { token } = useParams();
  const navigate = useNavigate();
  const botsApi = (process.env.REACT_APP_AUTH_API || 'http://localhost:5000/api/auth').replace('/api/auth', '/api/bots');
  React.useEffect(() => {
    if (!token) { navigate('/', { replace: true }); return; }
    fetch(botsApi + '/widget/' + token + '/settings')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (data.botId) navigate('/usertest/' + data.botId + '?mode=inline', { replace: true }); else navigate('/', { replace: true }); })
      .catch(() => navigate('/', { replace: true }));
  }, [token, navigate, botsApi]);
  return null;
}

function App() {
  return (
    <Routes>

      {/* Redirect root to talktokam-website */}
      <Route index element={<ExternalRedirect to={process.env.REACT_APP_WEBSITE_URL || "http://localhost:3002"} />} />

      {/* Standalone widget — no layout, used inside iframe embed */}
      <Route path="usertest/:chatId" element={<UserMessage />} />
      <Route path="usertoken/:token" element={<TokenRedirect />} />
      <Route path="inline/:token" element={<InlineRedirect />} />
      <Route path="agent-chat/:id" element={<AgentChat />} />

      <Route path="auth/callback" element={<AutoLogin />} />
      {/* ✅ WIDGET ROUTE – ROOT LEVEL */}
      {/* <Route path="/widget/ui:chatbotId" element={<WidgetLoader />} /> */}

      {/* Redirects */}
      <Route path="/chats" element={<Navigate to="/app/chats" replace />} />
      <Route path="/users" element={<Navigate to="/app/users" replace />} />
      <Route path="/answers" element={<Navigate to="/app/answers" replace />} />
      <Route path="/leads" element={<Navigate to="/app/leads" replace />} />
      <Route path="/analytics" element={<Navigate to="/app/analytics" replace />} />
      <Route path="/settings" element={<Navigate to="/app/settings" replace />} />
      <Route path="/setup" element={<Navigate to="/app/setup" replace />} />
      <Route path="/flow-setup" element={<Navigate to="/app/flow-setup" replace />} />
      <Route path="/install" element={<Navigate to="/app/install" replace />} />
      {/* /logout already handled directly above */}
      <Route path="/account" element={<Navigate to="/app/account" replace />} />
      <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/bots" element={<Navigate to="/dashboard" replace />} />
      <Route path="/conversations/:id" element={<Navigate to="/app/conversations/:id" replace />} />
      <Route path="/conversations" element={<Navigate to="/app/conversations" replace />} />

      {/* Agent standalone — no sidebar/layout */}
      <Route path="/live-chat" element={<LiveChat />} />

      {/* Standalone logout — outside PrivateRoute so it doesn't fight the redirect */}
      <Route path="/logout" element={<Logout />} />

      {/* Protected under /app */}
      <Route path="/app" element={<Layout />}>
        <Route element={<PrivateRoute />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="chats" element={<Chats />} />
          <Route path="users" element={<Users />} />
          <Route path="answers" element={<Answers />} />
          <Route path="leads" element={<Leads />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="settings" element={<Settings />} />
          <Route path="setup" element={<SetUp />} />
          <Route path="flow-setup" element={<FlowSetup />} />
          <Route path="install" element={<Install />} />
          <Route path="account" element={<Account />} />
          <Route path="conversations" element={<Conversations />} />
          <Route path="conversations/:id" element={<ConversationView />} />
          <Route path="bots" element={<Bots />} />
          <Route path="bot/:botId" element={<BotDetail />} />
          <Route path="ai-agents" element={<AIAgents />} />
          <Route path="ai-agents/:id" element={<AIAgentEdit />} />
          <Route path="ai-agents/:id/chats" element={<AIAgentChats />} />
          <Route path="templates" element={<Templates />} />
          <Route path="partners" element={<Partners />} />
          <Route path="referral" element={<Referral />} />
          <Route path="plans" element={<Plans />} />
          <Route path="users/manage" element={<UserManagement />} />
          <Route path="team" element={<TeamManagement />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
